import { useEffect, useState } from 'react';
import { useMsal } from 'src/auth/firebaseMsalCompat';

import { loginRequest } from '../../config/authConfig';
import { useFirebaseAuth } from '../../auth/FirebaseAuthContext';
import {
  getSubscriptionStatus,
  invalidateSubscriptionStatus,
  subscribeSubscriptionStatus,
  type RawSubscriptionStatus,
} from '../../services/subscriptionStatus';

export interface NavSubscription {
  tier: 'free' | 'starter' | 'pro';
  userType?: 'personal' | 'business';
}

const toNavSubscription = (
  data: RawSubscriptionStatus | null
): NavSubscription | null =>
  data
    ? { tier: data.subscriptionTier || 'free', userType: data.userType }
    : null;

/**
 * Lightweight subscription read for the always-mounted NavBar. Unlike
 * `useSubscriptionStatus`, this never fulfills Stripe checkout, never mutates
 * the URL, and never trips the error boundary — a transient status fetch must
 * not crash global chrome. Shares one deduplicated request (and cache) with
 * the page via `getSubscriptionStatus`, and subscribes so the badge follows
 * any refresh the page triggers. Clears that cache on sign-out. Returns null
 * until known.
 */
export const useNavSubscription = (
  isAuthenticated: boolean
): NavSubscription | null => {
  const { instance, inProgress } = useMsal();
  const { user } = useFirebaseAuth();
  // The nav hides its badge until the email is verified, but that must not
  // wipe another page's cache for the same signed-in account.
  const accountUid = user && !user.isAnonymous ? user.uid : null;
  const uid = isAuthenticated ? accountUid : null;
  const [sub, setSub] = useState<NavSubscription | null>(null);

  useEffect(() => {
    if (!accountUid && inProgress === 'none') {
      invalidateSubscriptionStatus();
    }
    if (inProgress !== 'none' || !uid) {
      setSub(null);
      return;
    }

    let cancelled = false;
    setSub(null);
    const unsubscribe = subscribeSubscriptionStatus((data, dataUid) => {
      if (cancelled) return;
      if (dataUid !== uid) {
        setSub(null);
        return;
      }
      setSub(toNavSubscription(data));
    });

    (async () => {
      try {
        const authResult = await instance.acquireTokenSilent({
          ...loginRequest,
        });
        const data = await getSubscriptionStatus(authResult.idToken, uid);
        if (!cancelled) setSub(toNavSubscription(data));
      } catch {
        // Silent on purpose — the nav must never crash on a status fetch.
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [instance, inProgress, isAuthenticated, uid, accountUid]);

  return sub;
};
